/**
 * context/sessionContext.tsx
 * The signed-in user/org from the platform's /v1/me. Powers the sidebar footer
 * identity + "Live" status. Never a hardcoded name.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getCurrentUser } from '../services/auth/auth';

export interface SessionUser {
  id?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
}
export interface SessionOrg {
  id?: string;
  name?: string;
}

interface SessionValue {
  user: SessionUser | null;
  org: SessionOrg | null;
  role: string | null;
  loading: boolean;
  fullName: string;
  initials: string;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue>({
  user: null,
  org: null,
  role: null,
  loading: true,
  fullName: '',
  initials: '',
  refresh: async () => {},
});

/** Maps the platform role slug to a human label for the sidebar. */
export function roleLabel(role: string | null): string {
  switch (role) {
    case 'org_admin':
      return 'Owner';
    case 'admin':
      return 'Admin';
    case 'platform_admin':
      return 'Platform Admin';
    case 'org_member':
    case 'user':
      return 'Member';
    default:
      return role ? role.replace(/_/g, ' ') : '';
  }
}

function toInitials(first?: string, last?: string, email?: string): string {
  const a = (first ?? '').trim();
  const b = (last ?? '').trim();
  if (a || b) return `${a.charAt(0)}${b.charAt(0)}`.toUpperCase() || a.slice(0, 2).toUpperCase();
  return (email ?? '?').slice(0, 2).toUpperCase();
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [org, setOrg] = useState<SessionOrg | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      type MeShape = {
        data?: { user?: SessionUser; org?: SessionOrg; memberships?: Array<{ role?: string }>; role?: string };
        user?: SessionUser;
        org?: SessionOrg;
        memberships?: Array<{ role?: string }>;
        role?: string;
      };
      const res = (await getCurrentUser()) as MeShape;
      const d = res.data ?? res;
      setUser(d?.user ?? null);
      setOrg(d?.org ?? null);
      setRole(d?.memberships?.[0]?.role ?? d?.role ?? null);
    } catch {
      setUser(null);
      setOrg(null);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  const initials = toInitials(user?.first_name, user?.last_name, user?.email);

  return (
    <SessionContext.Provider value={{ user, org, role, loading, fullName, initials, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);
